import { useQueryClient } from '@tanstack/react-query';
import defaultCaregiverPhoto from 'assets/experts-man-dark.png';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import dayjs from 'dayjs';
import { useCaregiverIdentity } from 'hooks/useCaregivers';
import { FailedMessage, removeFailedMessage } from 'hooks/useFailedMessages';
import useSendMessage from 'hooks/useSendMessage';
import { cn } from 'lib/utils';
import { CheckCheckIcon, CheckIcon, CircleAlertIcon, ClockIcon, ReplyIcon, RotateCwIcon } from 'lucide-react';
import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { GGApiSender } from 'services/api/models/GGApiMessage';
import { UserDTO } from 'services/api/models/UserDTO';
import { MessageSummary } from 'store/conversations/types';

export type MessageBubbleProps = {
  message: ConversationMessage;
  currentUser: UserDTO;
  conversationSummary?: MessageSummary;
  /** The sender's name was tapped (caregivers open their profile). */
  onTitleClick?: (senderId: string) => void;
  /** The quoted message was tapped. */
  onReplyClick?: (repliedToMessageId: number) => void;
  /**
   * A link in someone else's message was tapped by a caregiver, who confirms
   * before leaving the app. Without it links open directly.
   */
  onLinkClick?: (href: string) => void;
  /**
   * The messaging redesign's bubble: no avatar nor name in it, the sender and
   * time (or the delivery status) under the last bubble of a group.
   */
  redesign?: boolean;
  /** The last bubble of a group of messages (redesign only). */
  showFooter?: boolean;
};

const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

function isValidAndSafeUrl(urlString: string): boolean {
  try {
    const url = new URL(urlString.startsWith('www.') ? `https://${urlString}` : urlString);
    return ['http:', 'https:'].includes(url.protocol) && Boolean(url.hostname);
  } catch {
    return false;
  }
}

/** The message text with its URLs as links. */
function Linkified({ text, onLinkClick }: { text: string; onLinkClick?: (href: string) => void }) {
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, index) => {
        if (index % 2 === 0 || !isValidAndSafeUrl(part)) return <Fragment key={index}>{part}</Fragment>;
        const href = part.startsWith('www.') ? `https://${part}` : part;
        return (
          <a
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all underline"
            onClick={event => {
              if (onLinkClick) {
                event.preventDefault();
                onLinkClick(href);
              }
            }}
          >
            {part}
          </a>
        );
      })}
    </>
  );
}

function isRead(message: ConversationMessage, currentUser: UserDTO, conversationSummary?: MessageSummary) {
  if (!currentUser.is_caregiver) return false;
  const patientId = conversationSummary?.conversation.patient.id;
  const readStatus = conversationSummary?.conversation.read_statuses?.find(status => status.reader === patientId);
  return Boolean(readStatus && readStatus.last_read_datetime >= message.sent_at);
}

const senderPhoto = (sender?: GGApiSender) =>
  sender?.is_caregiver ? sender.photo_url || defaultCaregiverPhoto : sender?.photo_url;

/**
 * One message of a conversation (what react-chat-elements' `MessageBox` was):
 * the sender's bubble on the left with their name and avatar, the user's own
 * on the right, each on its `chat-*` tokens. `data-slot` and `data-position`
 * are the handles of the swipe-to-reply and context-menu hooks and of the
 * functional test suite.
 */
/** A message that could not be sent: outlined in peach, on muted peach. */
const failedBubbleClassName = 'border border-destructive-border bg-destructive-muted text-foreground';

/**
 * Under a message that could not be sent: send it again (it goes back to being
 * sent, at the end of the conversation), or drop it.
 */
function FailedMessageActions({ message }: { message: ConversationMessage }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: sendMessage } = useSendMessage();
  const { id, conversationId, message_text: messageText, replied_to_message: reply } = message as FailedMessage;

  return (
    <div data-slot="message-failed" className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 pt-1">
      <span className="flex items-center gap-1 text-xs font-medium text-destructive mr-2">
        <CircleAlertIcon aria-hidden className="size-3.5" />
        {t('chats.status.notSent')}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={async () => {
          await removeFailedMessage(queryClient, id);
          sendMessage({
            conversationId,
            messageText,
            replyingTo: reply && {
              messageIndex: -1,
              messageId: reply.id,
              messageText: reply.message_text,
              senderName: reply.sender.display_name,
            },
          });
        }}
      >
        <RotateCwIcon aria-hidden />
        {t('chats.status.retry')}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => removeFailedMessage(queryClient, id)}>
        {t('chats.status.dropFailed')}
      </Button>
    </div>
  );
}

/** The redesign's bubble (Figma "Message - Nova", "Bubble - Nova"), see `MessageBubbleProps.redesign`. */
function RedesignedMessageBubble({
  message,
  currentUser,
  conversationSummary,
  onTitleClick,
  onReplyClick,
  onLinkClick,
  showFooter,
}: MessageBubbleProps) {
  const { t } = useTranslation();
  const isMine = message.sender?.id === currentUser.id;
  const position = isMine ? 'right' : 'left';
  const reply = message.replied_to_message;
  const read = isRead(message, currentUser, conversationSummary);
  const time = dayjs(message.sent_at).format('HH:mm');
  // A caregiver signs with the name on her profile
  const caregiverSender = useCaregiverIdentity(message.sender?.is_caregiver && !isMine ? message.sender : undefined);
  const senderName = caregiverSender?.firstName || message.sender?.first_name || message.sender?.display_name;

  return (
    <div
      data-slot="message"
      data-position={position}
      data-message-id={message.id}
      data-pending={message.pending || undefined}
      data-failed={message.failed || undefined}
      className={cn('flex w-full touch-pan-y flex-col px-4', isMine ? 'items-end' : 'items-start')}
    >
      <div
        data-slot="message-bubble"
        data-position={position}
        className={cn(
          // The corner at the top, on the sender's side, points to her
          'group/bubble relative max-w-[85%] rounded-lg px-3.5 py-2.5 whitespace-pre-line will-change-transform',
          isMine
            ? 'rounded-tr-sm bg-chat-me text-chat-me-foreground'
            : 'rounded-tl-sm bg-chat-them text-chat-them-foreground',
          message.failed && failedBubbleClassName,
        )}
      >
        {/* The reply hint revealed while the bubble is swiped (see useSwipeToReply) */}
        <ReplyIcon
          aria-hidden
          className="pointer-events-none absolute top-1/2 -left-8 size-6 -translate-y-1/2 opacity-0 transition-opacity group-data-[swipe=active]/bubble:opacity-50"
        />
        {reply && !message.is_deleted && (
          <button
            type="button"
            data-slot="message-reply"
            className={cn(
              // the bar is a pseudo-element: a left border would curve with the corners
              'relative mb-2 block w-full cursor-pointer overflow-hidden rounded-md px-3 py-2 text-left text-sm before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-chat-them-title',
              isMine ? 'bg-accent text-foreground' : 'bg-card/60',
            )}
            onClick={() => onReplyClick?.(reply.id)}
          >
            <span className="block font-medium text-chat-them-title">{reply.sender.display_name}</span>
            <span className="line-clamp-2 text-muted-foreground">
              {reply.is_deleted ? t('chats.messageDeleted') : reply.message_text}
            </span>
          </button>
        )}
        {/* wrap-anywhere: a long URL or word breaks instead of stretching the bubble */}
        <div data-slot="message-text" className="text-base wrap-anywhere">
          {message.is_deleted ? (
            <em className="opacity-60">{t('chats.messageDeleted')}</em>
          ) : (
            <Linkified text={message.message_text} onLinkClick={!isMine ? onLinkClick : undefined} />
          )}
        </div>
        {/* `gg-edited-label` is a selector of the functional test suite. */}
        {message.edited_at && !message.is_deleted && (
          <span className="gg-edited-label block text-right text-xs opacity-60">{t('chats.messageEdited')}</span>
        )}
      </div>
      {message.failed && <FailedMessageActions message={message} />}
      {showFooter && !message.failed && (
        // `relative` keeps the time read to screen readers (`sr-only`, absolutely
        // positioned) inside the footer: placed against the page instead, it made
        // the whole page as tall as the conversation, and scrollable from the input
        <div
          data-slot="message-time"
          className="relative flex items-center gap-1 px-3 pt-2 pb-2 text-xs text-muted-foreground"
        >
          {isMine && message.pending ? (
            <>
              <ClockIcon aria-hidden className="size-3.5" />
              {t('messagingV3.conversation.sending')}
            </>
          ) : isMine ? (
            <>
              {read ? (
                <CheckCheckIcon aria-hidden className="size-3.5" />
              ) : (
                <CheckIcon aria-hidden className="size-3.5" />
              )}
              {read ? t('messagingV3.conversation.read') : t('messagingV3.conversation.delivered')}
              <span className="sr-only">{time}</span>
            </>
          ) : (
            <Trans
              i18nKey="messagingV3.conversation.sentBy"
              values={{ name: senderName, time }}
              components={{
                0: (
                  // The sender's name opens her profile (caregivers, for the patient)
                  <button
                    type="button"
                    data-slot="message-title"
                    className="cursor-pointer"
                    onClick={() => onTitleClick?.(message.sender.id)}
                  />
                ),
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function MessageBubble(props: MessageBubbleProps) {
  if (props.redesign) return <RedesignedMessageBubble {...props} />;
  return <ClassicMessageBubble {...props} />;
}

function ClassicMessageBubble({
  message,
  currentUser,
  conversationSummary,
  onTitleClick,
  onReplyClick,
  onLinkClick,
}: MessageBubbleProps) {
  const { t } = useTranslation();
  const isMine = message.sender?.id === currentUser.id;
  const position = isMine ? 'right' : 'left';
  // Another caregiver's message, seen by a caregiver: neither "me" nor the patient
  const isFromOtherCaregiver = currentUser.is_caregiver && !isMine && Boolean(message.sender?.is_caregiver);
  const photo = isMine ? undefined : senderPhoto(message.sender);
  const reply = message.replied_to_message;
  const read = isRead(message, currentUser, conversationSummary);

  return (
    <div
      data-slot="message"
      data-position={position}
      data-message-id={message.id}
      data-pending={message.pending || undefined}
      data-failed={message.failed || undefined}
      className={cn('flex w-full touch-pan-y flex-col px-4', isMine ? 'items-end' : 'items-start')}
    >
      <div
        data-slot="message-bubble"
        data-position={position}
        className={cn(
          'group/bubble relative max-w-[80%] rounded-lg px-3.5 pt-3.5 pb-3 whitespace-pre-line shadow-xs will-change-transform',
          isMine ? 'bg-chat-me text-chat-me-foreground' : 'bg-chat-them text-chat-them-foreground',
          isFromOtherCaregiver && 'bg-muted text-muted-foreground',
          message.failed && failedBubbleClassName,
        )}
      >
        {/* The reply hint revealed while the bubble is swiped (see useSwipeToReply) */}
        <ReplyIcon
          aria-hidden
          className="pointer-events-none absolute top-1/2 -left-8 size-6 -translate-y-1/2 opacity-0 transition-opacity group-data-[swipe=active]/bubble:opacity-50"
        />
        {!isMine && message.sender?.display_name && (
          <button
            type="button"
            data-slot="message-title"
            className="mb-1 flex cursor-pointer items-center gap-2 text-sm font-medium text-chat-them-title"
            onClick={() => onTitleClick?.(message.sender.id)}
          >
            {photo && (
              <Avatar size="sm">
                <AvatarImage src={photo} />
                <AvatarFallback />
              </Avatar>
            )}
            {message.sender.display_name}
          </button>
        )}
        {reply && !message.is_deleted && (
          <button
            type="button"
            data-slot="message-reply"
            className={cn(
              // the bar is a pseudo-element: a left border would curve with the corners
              'relative mb-2 block w-full cursor-pointer overflow-hidden rounded-md px-3 py-2 text-left text-sm before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-chat-them-title',
              isMine ? 'bg-accent text-foreground' : 'bg-muted',
            )}
            onClick={() => onReplyClick?.(reply.id)}
          >
            <span className="block font-medium text-chat-them-title">{reply.sender.display_name}</span>
            <span className="line-clamp-2 text-muted-foreground">
              {reply.is_deleted ? t('chats.messageDeleted') : reply.message_text}
            </span>
          </button>
        )}
        {/* wrap-anywhere: a long URL or word breaks instead of stretching the bubble */}
        <div data-slot="message-text" className="leading-snug wrap-anywhere">
          {message.is_deleted ? (
            <em className="text-base opacity-60">{t('chats.messageDeleted')}</em>
          ) : (
            <Linkified text={message.message_text} onLinkClick={!isMine ? onLinkClick : undefined} />
          )}
        </div>
        <div
          data-slot="message-time"
          className={cn(
            'mt-1 flex items-center justify-end gap-2 text-xs',
            isMine ? 'text-chat-me-foreground/70' : 'text-muted-foreground',
          )}
        >
          {/* `gg-edited-label` is a selector of the functional test suite. */}
          {message.edited_at && !message.is_deleted && (
            <span className="gg-edited-label">{t('chats.messageEdited')}</span>
          )}
          <span>{dayjs(message.sent_at).fromNow()}</span>
          {isMine &&
            (message.failed ? (
              <CircleAlertIcon aria-label={t('chats.status.notSent')} className="size-3.5 text-destructive" />
            ) : message.pending ? (
              <ClockIcon aria-label={t('chats.status.sending')} className="size-3.5" />
            ) : read ? (
              <CheckCheckIcon aria-label={t('chats.status.read')} className="size-3.5" />
            ) : (
              <CheckIcon aria-label={t('chats.status.sent')} className="size-3.5" />
            ))}
        </div>
      </div>
      {message.failed && <FailedMessageActions message={message} />}
    </div>
  );
}
